import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { DEFAULT_CURRENCY_CODE, ErrorHandler, LOCALE_ID, NgModule, inject, provideAppInitializer } from '@angular/core';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router, RouterModule } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { THEME_CONFIG } from '@lib/core';
import {
    IIM_PAGINATION_CONFIG,
    IimPaginationDefaultConfig,
    IimSpinnerModule,
    MatDayjsDateModule,
} from '@iim-angular/components';
import { AuthInterceptor, TokenRefreshInterceptor } from '@lib/auth';
import { MatMenuModule } from '@angular/material/menu';
import {
    ANALYTICS_USER_SOURCE,
    ApiUrlModule,
    ApiVersionMismatchModule,
    CacheInterceptor,
    ChunkLoadRecoveryService,
    ErrorInterceptor,
    excludeFromTreeShaking,
    getProvidersForAppInit,
    IAnalyticsUserSource,
    NotificationMessageModule,
    PageViewTrackerService,
    SentryUtilService,
    themeServiceConfig,
    VersionInterceptor,
} from '@lib/core';
import { EnvService, EnvServiceProvider } from '@lib/env';
import { createTranslateLoader, I18nInterceptor } from '@lib/i18n';
import { LayoutModule } from '@lib/layout';
import { metaReducers, reducers } from '@lib/store';
import {
    ConfirmationModule,
    ExplanationDialogModule,
    GlobalErrorHandler,
    HttpActivityInterceptor,
    Logger,
    SubscriptionHelper,
} from '@lib/util';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { MainComponent } from './app/main.component';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { DemandDataService } from '@lib/rfq';
import { MatTooltipModule } from '@angular/material/tooltip';
import { QrCodeModule } from '@lib/qr-code';
import { AppInitService } from '@lib/init';
import { InsertApiUrlComponent } from './app/insert-api-url/insert-api-url.component';
import { InvalidApiUrlComponent } from './app/invalid-api-url/invalid-api-url.component';
import { SharedBasketSidenavUiModule } from '@lib/basket-sidenav';
import { SHOW_BUTTON } from '@lib/dashboard/widgets/ui';
import { BreadcrumbComponent } from '@lib/shared/breadcrumbs';
import { InvoiceBasicApprovalFormComponent } from '@lib/invoice';
import { BASKET_ADDRESS_DIALOG_COMPONENT } from '@lib/shared/basket-service';
import { BasketAddressSelectionComponent } from '@lib/basket/ui';
import { BULK_ACCOUNTING_DIALOG_COMPONENT } from '@lib/accounting';
import { BulkAccountingDialogComponent } from '@lib/shared/bulk-accounting-dialog';
import { ClientDataService } from '@lib/client';
import { UserService } from '@lib/user';
import { firstValueFrom } from 'rxjs';

function analyticsUserSource(): IAnalyticsUserSource {
    const userService = inject(UserService);
    const clientDataService = inject(ClientDataService);

    return {
        currentUser: async () => ({
            userId: (await userService.getMyself()).id,
            tenantLogin: (await firstValueFrom(clientDataService.getMyCompany())).login,
        }),
    };
}

excludeFromTreeShaking(InvoiceBasicApprovalFormComponent);

@NgModule({
    declarations: [AppComponent, SubscriptionHelper, MainComponent, InsertApiUrlComponent, InvalidApiUrlComponent],
    bootstrap: [AppComponent],
    imports: [
        CommonModule,
        AppRoutingModule,
        RouterModule,
        BrowserModule,
        BrowserAnimationsModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: createTranslateLoader,
                deps: [HttpClient],
            },
        }),
        LayoutModule,
        // ngrx modules
        StoreRouterConnectingModule.forRoot(),
        StoreModule.forRoot(reducers, {
            metaReducers,
            runtimeChecks: {
                strictStateImmutability: false,
                strictActionImmutability: false,
                strictStateSerializability: false,
                strictActionSerializability: false,
            },
        }),
        ...(EnvService.enableStoreDevtools
            ? [
                  StoreDevtoolsModule.instrument({
                      maxAge: 25, // Retains last 25 states
                      logOnly: EnvService.production, // Restrict extension to log-only mode
                  }),
              ]
            : []),
        ServiceWorkerModule.register('ngsw-worker.js', { enabled: EnvService.enableServiceWorker }),
        MatSelectModule,
        IimSpinnerModule,
        MatDayjsDateModule,
        MatDatepickerModule,
        MatDialogModule,
        MatBottomSheetModule,
        MatSidenavModule,
        MatSnackBarModule,
        NotificationMessageModule,
        MatAutocompleteModule,
        MatChipsModule,
        MatTooltipModule,
        MatMenuModule,
        ApiVersionMismatchModule,
        ExplanationDialogModule,
        ConfirmationModule,
        QrCodeModule,
        ApiUrlModule,
        SharedBasketSidenavUiModule,
        BreadcrumbComponent,
    ],
    providers: [
        {
            provide: LOCALE_ID,
            useValue: 'de-DE',
        },
        {
            provide: THEME_CONFIG,
            useValue: themeServiceConfig,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: I18nInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: CacheInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ErrorInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: VersionInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpActivityInterceptor,
            multi: true,
        },
        {
            // innermost interceptor: retries 401s once via token refresh BEFORE the
            // error interceptor sees the 401 and performs the logout (EPROC-11254)
            provide: HTTP_INTERCEPTORS,
            useClass: TokenRefreshInterceptor,
            multi: true,
        },
        {
            provide: ErrorHandler,
            useClass: GlobalErrorHandler,
        },
        { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
        EnvServiceProvider,
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler(),
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        provideAppInitializer(() => {
            const initializerFn = (
                (appInitService: AppInitService) => () =>
                    appInitService.init()
            )(inject(AppInitService));
            return initializerFn();
        }),
        provideAppInitializer(async () => inject(Sentry.TraceService)),
        provideAppInitializer(() => inject(ChunkLoadRecoveryService).listen()),
        provideAppInitializer(() => inject(PageViewTrackerService).listen()),
        { provide: ANALYTICS_USER_SOURCE, useFactory: analyticsUserSource },
        { provide: SHOW_BUTTON, useValue: '' },
        DemandDataService,
        ...getProvidersForAppInit(),
        provideHttpClient(withInterceptorsFromDi()),
        {
            provide: IIM_PAGINATION_CONFIG,
            useFactory: () => new IimPaginationDefaultConfig(-100, 'scroll-container'),
        },
        { provide: BASKET_ADDRESS_DIALOG_COMPONENT, useValue: BasketAddressSelectionComponent },
        { provide: BULK_ACCOUNTING_DIALOG_COMPONENT, useValue: BulkAccountingDialogComponent },
    ],
})
export class AppModule {
    constructor() {
        const sentryUtilService = inject(SentryUtilService);

        sentryUtilService.init();
        Logger.init();
    }
}
